import { Box, IconButton, Paper, Typography } from "@mui/material";
import Add from "@mui/icons-material/Add";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Today from "@mui/icons-material/Today";
import moment from "moment";
import { useAsync2 } from "nate-react-api-helpers";
import { ReactNode, useEffect, useRef } from "react";
import { api } from "../../../api/API";
import { Task } from "../../../api/Tasks";
import { dateFormat, useToday } from "../../../misc/dates";
import { useTaskFeed } from "../../../misc/Feed";
import { PaletteGate } from "../../../misc/PaletteGate";
import { TinyButton } from "../../../misc/TinyButton";
import { useUrlState } from "../../../misc/urlParams";
import { useWorkspace } from "../../../misc/Workspace";
import { parseAnchor } from "../calendar/range";
import { DayWeekToggle } from "../DayWeekToggle";
import { NewTaskFab } from "../NewTaskFab";
import { DayTaskRow } from "../day/DayTaskRow";
import { MobileTaskEditor } from "../day/MobileTaskEditor";
import { ProjectDrawer } from "../day/ProjectDrawer";
import { SortableDayTasks } from "../day/SortableDayTasks";
import { groupDayTasks } from "../dayGroups";
import { useOpenTask } from "../taskUrl";
import { useCompleteTask } from "../useCompleteTask";

// MobileWeekPage is the week on a phone: seven days stacked, Monday first, since the
// week grid doesn't fit across a phone
export function MobileWeekPage() {
    const url = useUrlState();
    const ws = useWorkspace();
    const today = useToday();

    const anchor = moment(parseAnchor(url.text("date")), dateFormat);
    const start = anchor.clone().startOf("isoWeek");
    const end = start.clone().add(6, "days");
    const from = start.format(dateFormat);
    const to = end.format(dateFormat);
    const days = Array.from({ length: 7 }, (_, index) => start.clone().add(index, "days"));

    const opened = useOpenTask();
    const taskId = opened.id;

    // the server pushes the list again after every change, so nothing here reloads
    const list = useTaskFeed({
        dueFrom: from,
        dueTo: to,
        today: today,
        includeCompletedChecklist: true,
        team: ws.team || undefined,
    });

    const listed = taskId ? (list.tasks.find(task => task.id === taskId) || null) : null;
    const fetched = useAsync2(async (input: { id: number }) =>
        input.id ? api.tasks.get({ id: input.id }) : null,
        { id: listed ? 0 : taskId }, [listed ? 0 : taskId]);

    // a new task is due on the day the url names, which is a day of the week on screen
    const editing: Partial<Task> | null = taskId
        ? (listed || fetched.result)
        : (opened.isNew ? { dueDate: anchor.format(dateFormat) } : null);

    const toggle = useCompleteTask();

    const openTask = opened.open;
    const closeTask = opened.close;

    // stepping through the weeks replaces rather than pushes, so the back button walks
    // out of the page instead of back through every week on the way in
    const step = (amount: number) => url.set({
        date: start.clone().add(amount, "week").format(dateFormat),
    }, { replace: true });

    // the page opens on today, when today is in the week on screen
    const todaySection = useRef<HTMLDivElement>(null);
    const scrolled = useRef("");
    useEffect(() => {
        if (list.loading || scrolled.current === from) return;

        scrolled.current = from;
        todaySection.current?.scrollIntoView({ block: "start" });
    }, [list.loading, from]);

    const isThisWeek = moment().isBetween(start, end, "day", "[]");

    const renderTask = (task: Task, grip?: ReactNode) => (
        <DayTaskRow
            key={task.id}
            task={task}
            grip={grip}
            disabled={toggle.loading}
            onClick={() => openTask(task)}
            onToggle={checked => toggle.callback({ id: task.id, completed: checked })}
        />
    );

    return (
        <PaletteGate>
            <Box sx={{ pb: 2 }}>
                <Paper elevation={0} square sx={{
                    position: "sticky", top: 0, zIndex: 3,
                    display: "flex", alignItems: "center", gap: 1, px: 1, py: 1,
                    borderBottom: 1, borderColor: "divider",
                }}>
                    <IconButton disabled={isThisWeek} aria-label="Go to this week"
                                onClick={() => url.set({ date: moment().format(dateFormat) }, { replace: true })}>
                        <Today />
                    </IconButton>
                    <IconButton onClick={() => step(-1)} aria-label="Previous week"><ChevronLeft /></IconButton>
                    <Typography variant="subtitle1" align="center" noWrap sx={{ flex: 1, fontWeight: 600 }}>
                        {start.format("MMM D")} – {end.format(start.isSame(end, "month") ? "D" : "MMM D")}
                    </Typography>
                    <IconButton onClick={() => step(1)} aria-label="Next week"><ChevronRight /></IconButton>
                    <DayWeekToggle value="week" date={from} />
                </Paper>

                <Box sx={{ px: 1, py: 1, "&:empty": { display: "none" } }}>
                    {list.LoadingOrErrorElement}
                    {fetched.LoadingOrErrorElement}
                </Box>

                {days.map(day => {
                    const key = day.format(dateFormat);
                    const isToday = key === today;
                    const tasks = list.tasks.filter(task => task.showOn === key);
                    const { done, open, carried } = groupDayTasks(tasks);
                    const openCount = tasks.filter(task => !task.completedAt).length;

                    return (
                        <Box key={key} ref={isToday ? todaySection : undefined} sx={{ scrollMarginTop: 56 }}>
                            <Box sx={{ display: "flex", alignItems: "center", px: 1.5, pt: 1.5, pb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{
                                    flex: 1,
                                    color: isToday ? "primary.main" : "text.primary",
                                }}>
                                    {day.format("ddd, MMM D")}
                                    <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                                        {" · " + openCount + " open"}
                                    </Box>
                                </Typography>
                                <TinyButton variant="outlined" color="primary"
                                            startIcon={<Add sx={{ fontSize: 16 }} />}
                                            aria-label={"New task on " + day.format("dddd")}
                                            onClick={() => openTask({ dueDate: key })}>
                                    Add
                                </TinyButton>
                            </Box>

                            <Paper variant="outlined" square sx={{ borderLeft: 0, borderRight: 0 }}>
                                {done.map(task => renderTask(task))}

                                {/* a drag stays inside its day */}
                                <SortableDayTasks tasks={open} renderRow={renderTask} />

                                {carried.length > 0 && (
                                    <Typography variant="caption" component="div" sx={{
                                        px: 1.5, pt: 1.5, pb: 0.5,
                                        color: "text.disabled",
                                        fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                                        textTransform: "uppercase",
                                    }}>
                                        From previous
                                    </Typography>
                                )}
                                {carried.map(task => renderTask(task))}

                                {tasks.length === 0 && (
                                    <Typography variant="caption" color="text.disabled" component="div" sx={{ px: 1.5, py: 1 }}>
                                        Nothing due
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                    );
                })}

                {/* a task made here is due today when today is in the week, else on its
                    first day */}
                <NewTaskFab onClick={() => openTask({ dueDate: isThisWeek ? today : from })} />
                <ProjectDrawer dueFrom={from} dueTo={to} today={today} />

                {editing && (
                    <MobileTaskEditor
                        key={editing.id || "new"}
                        value={editing}
                        onClose={closeTask}
                        onSaved={() => {}}
                    />
                )}
            </Box>
        </PaletteGate>
    );
}
